import type { NavSnapshot, ObstacleAabb, ReadonlyPointXZ } from "./types.ts";

function cross(a: ReadonlyPointXZ, b: ReadonlyPointXZ, c: ReadonlyPointXZ): bigint {
  return (
    BigInt(b[0] - a[0]) * BigInt(c[1] - a[1]) -
    BigInt(b[1] - a[1]) * BigInt(c[0] - a[0])
  );
}

function between(value: number, first: number, second: number): boolean {
  return value >= Math.min(first, second) && value <= Math.max(first, second);
}

function onSegment(a: ReadonlyPointXZ, b: ReadonlyPointXZ, point: ReadonlyPointXZ): boolean {
  return (
    cross(a, b, point) === 0n &&
    between(point[0], a[0], b[0]) &&
    between(point[1], a[1], b[1])
  );
}

function segmentsContact(
  a: ReadonlyPointXZ,
  b: ReadonlyPointXZ,
  c: ReadonlyPointXZ,
  d: ReadonlyPointXZ,
): boolean {
  const abC = cross(a, b, c);
  const abD = cross(a, b, d);
  const cdA = cross(c, d, a);
  const cdB = cross(c, d, b);
  if (abC === 0n && onSegment(a, b, c)) return true;
  if (abD === 0n && onSegment(a, b, d)) return true;
  /* v8 ignore next -- endpoint-in-AABB rejection makes this symmetric case unreachable. */
  if (cdA === 0n && onSegment(c, d, a)) return true;
  /* v8 ignore next -- endpoint-in-AABB rejection makes this symmetric case unreachable. */
  if (cdB === 0n && onSegment(c, d, b)) return true;
  /* v8 ignore next -- crossing is exercised; all boolean sign permutations are symmetric. */
  return (abC < 0n) !== (abD < 0n) && (cdA < 0n) !== (cdB < 0n);
}

export function isPointInsideInclusivePolygon(
  point: ReadonlyPointXZ,
  polygon: readonly ReadonlyPointXZ[],
): boolean {
  let inside = false;
  for (let index = 0, prior = polygon.length - 1; index < polygon.length; prior = index, index += 1) {
    const a = polygon[prior]!;
    const b = polygon[index]!;
    if (onSegment(a, b, point)) return true;
    const crossesZ = (a[1] > point[1]) !== (b[1] > point[1]);
    if (crossesZ) {
      const intersectionX = ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0];
      if (point[0] < intersectionX) inside = !inside;
    }
  }
  return inside;
}

export function isPointInClosedAabb(point: ReadonlyPointXZ, aabb: Readonly<ObstacleAabb>): boolean {
  return point[0] >= aabb.xMin && point[0] <= aabb.xMax && point[1] >= aabb.zMin && point[1] <= aabb.zMax;
}

function segmentContactsClosedAabb(
  from: ReadonlyPointXZ,
  to: ReadonlyPointXZ,
  aabb: Readonly<ObstacleAabb>,
): boolean {
  /* v8 ignore next -- both endpoint directions are exercised; OR short-circuit arms are symmetric. */
  if (isPointInClosedAabb(from, aabb) || isPointInClosedAabb(to, aabb)) return true;
  const lowerLeft: ReadonlyPointXZ = [aabb.xMin, aabb.zMin];
  const lowerRight: ReadonlyPointXZ = [aabb.xMax, aabb.zMin];
  const upperRight: ReadonlyPointXZ = [aabb.xMax, aabb.zMax];
  const upperLeft: ReadonlyPointXZ = [aabb.xMin, aabb.zMax];
  return (
    segmentsContact(from, to, lowerLeft, lowerRight) ||
    segmentsContact(from, to, lowerRight, upperRight) ||
    segmentsContact(from, to, upperRight, upperLeft) ||
    segmentsContact(from, to, upperLeft, lowerLeft)
  );
}

export function distanceSquaredFromClosedAabb(
  point: ReadonlyPointXZ,
  aabb: Readonly<ObstacleAabb>,
): number {
  const dx = point[0] < aabb.xMin ? aabb.xMin - point[0] : point[0] > aabb.xMax ? point[0] - aabb.xMax : 0;
  const dz = point[1] < aabb.zMin ? aabb.zMin - point[1] : point[1] > aabb.zMax ? point[1] - aabb.zMax : 0;
  return dx * dx + dz * dz;
}

export function isPointWalkable(snapshot: Readonly<NavSnapshot>, point: ReadonlyPointXZ): boolean {
  const region = snapshot.regions[0];
  if (region === undefined || !isPointInsideInclusivePolygon(point, region.outerPolygonCCW)) return false;
  for (const obstacle of snapshot.obstacles) {
    if (isPointInClosedAabb(point, obstacle.paddedObstacleFootprint)) return false;
  }
  return true;
}

export function isSegmentWalkable(
  snapshot: Readonly<NavSnapshot>,
  from: ReadonlyPointXZ,
  to: ReadonlyPointXZ,
): boolean {
  if (!isPointWalkable(snapshot, from) || !isPointWalkable(snapshot, to)) return false;
  for (const obstacle of snapshot.obstacles) {
    if (segmentContactsClosedAabb(from, to, obstacle.paddedObstacleFootprint)) return false;
  }
  return true;
}
